/**
 * SidebarStep2 - Event Details (v2 Sidebar Layout)
 * 
 * THE FORK POINT: Calculation Mode selection determines the entire workflow.
 * - Standard BI: Historical BI engine with V-Test causation + RTP recovery adjustment
 * - Future Forced Closure: Pro forma projection with Monte Carlo ranges
 * 
 * This component wraps the existing Step2 form logic with the new sidebar layout.
 * EXACT same functionality - only layout changes.
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle, Info, Calendar, Building, Zap, Check, HelpCircle, Target, Clock, FileText, ChevronDown } from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import { apiFetch } from '../../../utils/api';
import { useAuth } from '../../../contexts/TestAuthContext';
import { toast } from 'sonner';

interface EventTemplate {
  id: string;
  name: string;
  event_type: string;
  description?: string;
  calculation_mode: string;
  default_closure_months: number;
  continuation_rates: Record<string, number>;
  common_extra_expenses: string[];
  attorney_notes?: string;
  is_active: boolean;
  is_system: boolean;
}

interface SidebarStep2Props {
  goToStep?: (step: number) => void;
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  isSaving?: boolean;
  lastSaved?: Date | null;
  onSave?: () => void;
  highestCompletedStep?: number;
  backendCompletionPercent?: number;
  claimId?: string;
  chromeless?: boolean;
  onDataUpdate?: (data: any) => void;
}

/**
 * Derive the forced-closure period from the claim's confirmed dates
 * (event_intelligence), mirroring the backend derive_closure_period(). The closure
 * spans the loss month through the reopening month INCLUSIVE (the reopening month is
 * a partial closure month). Returns null when the confirmed dates are absent.
 */
function deriveClosureFromEventIntel(data: any): { startMonth: string; reopenMonth: string; months: number } | null {
  const ev = data?.event_intelligence || {};
  const eventDate: string | undefined = ev.confirmed_event_date || ev.incident_date;
  const reopenDate: string | undefined = ev.confirmed_reopening_date || ev.period_of_restoration_end_date;
  if (!eventDate || !reopenDate || eventDate.length < 7 || reopenDate.length < 7) return null;
  const startMonth = eventDate.slice(0, 7);
  const reopenMonth = reopenDate.slice(0, 7);
  const [sy, sm] = [parseInt(startMonth.slice(0, 4)), parseInt(startMonth.slice(5, 7))];
  const [ry, rm] = [parseInt(reopenMonth.slice(0, 4)), parseInt(reopenMonth.slice(5, 7))];
  const months = (ry * 12 + rm) - (sy * 12 + sm) + 1;
  if (!Number.isFinite(months) || months < 1) return null;
  return { startMonth, reopenMonth, months };
}

function formatMonth(ym: string): string {
  const [y, m] = ym.split('-').map((v) => parseInt(v));
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[(m - 1) % 12]} ${y}`;
}

export default function SidebarStep2({ onNext, onBack, data, isSaving, lastSaved, onSave, goToStep, highestCompletedStep, backendCompletionPercent, claimId, chromeless, onDataUpdate }: SidebarStep2Props) {
  const { getToken } = useAuth();
  const [formData, setFormData] = useState({
    business_type: data.business_type || '',
    event_type: data.event_type || '',
    event_start_date: data.event_start_date || '',
    event_end_date: data.event_end_date || '',
    fema_declaration_number: data.fema_declaration_number || '',
    fema_notes: data.fema_notes || '',
    calculation_mode: data.calculation_mode || 'standard_bi',
    closure_duration_months: data.closure_duration_months || 36,
    applied_template_id: data.applied_template_id || '',
    continuation_rates: data.continuation_rates || {}
  });
  
  const [showEventInfo, setShowEventInfo] = useState(false);
  const [showModeInfo, setShowModeInfo] = useState<'standard' | 'future' | null>(null);
  const [activeSection, setActiveSection] = useState('calc_mode');
  const [validationError, setValidationError] = useState<string | null>(null);
  
  // Event Templates state
  const [templates, setTemplates] = useState<EventTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EventTemplate | null>(null);

  // Fetch event templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const token = await getToken();
        const response = await apiFetch<EventTemplate[]>('/api/v1/event-templates/', {
          headers: { Authorization: `Bearer ${token}` },
          showErrorToast: false
        });
        // Filter to only show active templates
        const activeTemplates = (response || []).filter(t => t.is_active);
        setTemplates(activeTemplates);
        
        // If data has applied_template_id, find and set the selected template
        if (data.applied_template_id) {
          const applied = activeTemplates.find(t => t.id === data.applied_template_id);
          if (applied) {
            setSelectedTemplate(applied);
          }
        }
      } catch (error) {
        console.error('Error fetching event templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, [getToken, data.applied_template_id]);

  // Apply a template to the form
  const applyTemplate = (template: EventTemplate) => {
    const newFormData = {
      ...formData,
      event_type: template.event_type,
      calculation_mode: template.calculation_mode,
      closure_duration_months: template.default_closure_months,
      continuation_rates: template.continuation_rates || {},
      applied_template_id: template.id
    };
    
    setFormData(newFormData);
    setSelectedTemplate(template);
    setShowTemplateDropdown(false);
    
    // Propagate to parent immediately
    if (onDataUpdate) {
      onDataUpdate(newFormData);
    }
    
    toast.success(`Applied template: ${template.name}`);
  };

  // Clear template selection
  const clearTemplate = () => {
    setSelectedTemplate(null);
    setFormData(prev => ({
      ...prev,
      applied_template_id: ''
    }));
  };

  const businessTypes = [
    'Restaurant', 
    'Retail', 
    'Services', 
    'Manufacturing', 
    'Nonprofit/Charity',
    'Healthcare',
    'Hospitality/Tourism',
    'Construction',
    'Other'
  ];
  
  const eventTypes = [
    {
      value: 'hurricane',
      label: 'Hurricane / Tropical Storm',
      threshold: '20%',
      description: 'NOAA-verified storm damage, flooding, evacuation losses'
    },
    {
      value: 'government_shutdown',
      label: 'Government Shutdown / Furlough',
      threshold: '15%',
      description: 'Federal contract delays, reduced government spending impact'
    },
    {
      value: 'tariffs',
      label: 'Tariffs / Trade War',
      threshold: '18%',
      description: 'Import cost increases, supply chain disruptions (18.6% avg)'
    },
    {
      value: 'wildfire',
      label: 'Wildfire / Fire Damage',
      threshold: '25%',
      description: 'Fire damage, smoke evacuation, tourism decline ($89B 2025)'
    },
    {
      value: 'pandemic',
      label: 'Pandemic / Public Health Emergency',
      threshold: '8%',
      description: 'Lockdowns, capacity restrictions (COVID-19 standard)'
    },
    {
      value: 'flood',
      label: 'Flood / Storm Surge',
      threshold: '22%',
      description: 'FEMA-verified flooding, evacuation, infrastructure damage'
    },
    {
      value: 'earthquake',
      label: 'Earthquake',
      threshold: '30%',
      description: 'USGS-verified seismic damage, utility disruption'
    },
    {
      value: 'civil_unrest',
      label: 'Civil Unrest / Riots',
      threshold: '20%',
      description: 'Property damage, curfews, reduced foot traffic'
    },
    {
      value: 'cyber_attack',
      label: 'Cyber Attack / Ransomware',
      threshold: '15%',
      description: 'System downtime, data breach, operational disruption'
    },
    {
      value: 'toxic_contamination',
      label: 'Toxic Contamination (PFAS/Chemical)',
      threshold: '0%',
      description: 'Forced closure for toxic remediation - requires Future Closure mode',
      suggestFutureClosure: true
    },
    {
      value: 'plant_explosion',
      label: 'Plant Explosion / Industrial Accident',
      threshold: '0%',
      description: 'Permanent closure due to industrial disaster',
      suggestFutureClosure: true
    },
    {
      value: 'train_derailment',
      label: 'Train Derailment / Transportation Disaster',
      threshold: '0%',
      description: 'Area contamination from transportation accident',
      suggestFutureClosure: true
    },
    {
      value: 'custom',
      label: 'Custom Event (User-Defined)',
      threshold: '12%',
      description: 'Flexible parameters for any business disruption'
    }
  ];

  const selectedEvent = eventTypes.find(e => e.value === formData.event_type);
  const isFutureClosureMode = formData.calculation_mode === 'future_closure';

  // Sync formData with parent's data prop.
  // Depend on the specific primitive fields we read, NOT the whole `data`
  // object — the parent recreates `data` on every render, which previously
  // re-fired this effect and triggered setFormData → cascade.
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      business_type: data.business_type || prev.business_type,
      event_type: data.event_type || prev.event_type,
      event_start_date: data.event_start_date || prev.event_start_date,
      event_end_date: data.event_end_date || prev.event_end_date,
      fema_declaration_number: data.fema_declaration_number || prev.fema_declaration_number,
      fema_notes: data.fema_notes || prev.fema_notes,
      calculation_mode: data.calculation_mode || prev.calculation_mode,
      closure_duration_months: data.closure_duration_months || prev.closure_duration_months
    }));
  }, [
    data.business_type,
    data.event_type,
    data.event_start_date,
    data.event_end_date,
    data.fema_declaration_number,
    data.fema_notes,
    data.calculation_mode,
    data.closure_duration_months,
  ]);

  const handleEventTypeChange = (eventType: string) => {
    const newData = { ...formData, event_type: eventType };

    // Auto-suggest Future Closure for certain event types
    const event = eventTypes.find(e => e.value === eventType);
    if (event?.suggestFutureClosure && formData.calculation_mode === 'standard_bi') {
      // Show suggestion but don't auto-change
    }

    setFormData(newData);
    setValidationError(null);
  };

  const handleSubmit = () => {
    const missingFields = [];

    if (!formData.event_type) missingFields.push('Disruption Event Type');
    if (!formData.business_type) missingFields.push('Business Type');

    if (missingFields.length > 0) {
      setValidationError(`Please complete the following required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    console.log(`✅ Calculation mode: ${formData.calculation_mode}`);
    
    onNext({
      ...formData,
      calculation_mode: formData.calculation_mode
    });
  };

  // Calculate completion
  const requiredComplete = formData.event_type && formData.business_type;
  const completionPercent = Math.round(
    (14 + // Step 1
    (formData.calculation_mode ? 3 : 0) +
    (formData.event_type ? 5 : 0) +
    (formData.business_type ? 5 : 0) +
    (formData.event_start_date && formData.event_end_date ? 1 : 0))
  );

  // Build QA items
  const qaItems = [];
  if (formData.calculation_mode) {
    qaItems.push({ 
      id: 'mode', 
      label: isFutureClosureMode ? 'Future Closure' : 'Standard BI', 
      status: 'ok' as const 
    });
  }
  if (!formData.event_type) qaItems.push({ id: 'event', label: 'Event Type', status: 'missing' as const });
  if (!formData.business_type) qaItems.push({ id: 'business', label: 'Business Type', status: 'missing' as const });

  // Build sections - mode-aware
  const sections = [
    { 
      id: 'template', 
      label: 'Event Template', 
      isActive: activeSection === 'template',
      isComplete: !!selectedTemplate,
      onClick: () => setActiveSection('template')
    },
    { 
      id: 'calc_mode', 
      label: 'Calc Mode', 
      isActive: activeSection === 'calc_mode',
      isComplete: !!formData.calculation_mode,
      onClick: () => setActiveSection('calc_mode')
    },
    { 
      id: 'event', 
      label: 'Event Type', 
      isActive: activeSection === 'event',
      isComplete: !!formData.event_type,
      onClick: () => setActiveSection('event')
    },
    { 
      id: 'business', 
      label: 'Business Type', 
      isActive: activeSection === 'business',
      isComplete: !!formData.business_type,
      onClick: () => setActiveSection('business')
    },
    { 
      id: 'dates', 
      label: 'Event Dates', 
      isActive: activeSection === 'dates',
      isComplete: !!(formData.event_start_date && formData.event_end_date),
      onClick: () => setActiveSection('dates')
    },
  ];

  // Add closure duration for Future Closure mode
  if (isFutureClosureMode) {
    sections.splice(4, 0, {
      id: 'closure_duration',
      label: 'Closure Duration',
      isActive: activeSection === 'closure_duration',
      isComplete: !!formData.closure_duration_months,
      onClick: () => setActiveSection('closure_duration')
    });
  }

  return (
    <WizardV2Layout
      currentStep={2}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Choose Claim Type & Event Details"
      stepSubtitle="Select your calculation mode and event type to configure the claim analysis."
      completionPercent={backendCompletionPercent ?? completionPercent}
      calculationMode={formData.calculation_mode as 'standard_bi' | 'future_closure'}
      sections={sections}
      qaItems={qaItems}
      nextAction={qaItems.find(q => q.status === 'missing') ? {
        label: `Select ${qaItems.find(q => q.status === 'missing')?.label}`,
        description: 'Required to continue'
      } : undefined}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={handleSubmit}
      claimId={claimId}
      chromeless={chromeless}
      nextLabel="Continue to Financial Data"
      isNextDisabled={!requiredComplete}
    >
      <div className="p-6 lg:p-8">

        {validationError && (
          <p className="cs-meta mb-4" style={{ color: 'var(--color-needs-human-text)' }}>{validationError}</p>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            EVENT TEMPLATE SELECTOR - Quick setup from pre-configured templates
        ═══════════════════════════════════════════════════════════════════ */}
        {templates.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              <h2 className="cs-h2">Quick Setup with Event Template</h2>
              <span className="cs-chip px-2 py-0.5 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>Optional</span>
            </div>

            <div className="rounded-xl p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <p className="cs-body mb-3">
                Select a pre-configured template to auto-fill event type, calculation mode, and default continuation rates.
              </p>

              {/* Template Selector Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors"
                  style={{ background: 'var(--color-bg-elevated)', border: `1px solid ${selectedTemplate ? 'var(--color-brand)' : 'var(--color-border)'}` }}
                >
                  <div className="flex items-center gap-3">
                    {selectedTemplate ? (
                      <>
                        <FileText className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                        <div className="text-left">
                          <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{selectedTemplate.name}</p>
                          <p className="cs-meta">
                            {selectedTemplate.calculation_mode === 'future_closure' ? 'Future Closure' : 'Standard BI'} • {selectedTemplate.default_closure_months} months
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <span style={{ color: 'var(--color-text-dim)' }}>Select a template...</span>
                      </>
                    )}
                  </div>
                  <ChevronDown className={`w-5 h-5 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} style={{ color: 'var(--color-text-dim)' }} />
                </button>

                {/* Dropdown Menu */}
                {showTemplateDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                    {/* Clear Selection Option */}
                    {selectedTemplate && (
                      <button
                        type="button"
                        onClick={() => { clearTemplate(); setShowTemplateDropdown(false); }}
                        className="w-full px-4 py-2 text-left cs-meta"
                        style={{ borderBottom: '1px solid var(--color-border-sub)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        ✕ Clear selection (manual setup)
                      </button>
                    )}

                    {loadingTemplates ? (
                      <div className="px-4 py-3 cs-meta">Loading templates...</div>
                    ) : templates.length === 0 ? (
                      <div className="px-4 py-3 cs-meta">No templates available</div>
                    ) : (
                      templates.map((template) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => applyTemplate(template)}
                          className="w-full px-4 py-3 text-left transition-colors"
                          style={{ background: selectedTemplate?.id === template.id ? 'var(--color-needs-human-tint)' : 'transparent' }}
                          onMouseEnter={(e) => { if (selectedTemplate?.id !== template.id) e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                          onMouseLeave={(e) => { if (selectedTemplate?.id !== template.id) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{template.name}</p>
                              <p className="cs-meta">
                                {template.event_type} • {template.calculation_mode === 'future_closure' ? 'Future Closure' : 'Standard BI'} • {template.default_closure_months}mo
                              </p>
                              {template.description && (
                                <p className="cs-meta mt-1" style={{ color: 'var(--color-text-dim)' }}>{template.description}</p>
                              )}
                            </div>
                            {template.is_system && (
                              <span className="cs-chip px-2 py-0.5 rounded" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>System</span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {selectedTemplate && (
                <div className="mt-3 p-3 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                  <p className="cs-label mb-2" style={{ color: 'var(--color-needs-human-text)' }}>Template applied:</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                      Mode: {selectedTemplate.calculation_mode === 'future_closure' ? 'Future Closure' : 'Standard BI'}
                    </span>
                    <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                      Duration: {selectedTemplate.default_closure_months} months
                    </span>
                    {selectedTemplate.continuation_rates && Object.keys(selectedTemplate.continuation_rates).length > 0 && (
                      <span className="cs-chip px-2 py-1 rounded" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                        {Object.keys(selectedTemplate.continuation_rates).length} expense rates preset
                      </span>
                    )}
                  </div>
                  {selectedTemplate.attorney_notes && (
                    <p className="mt-2 cs-meta italic">"{selectedTemplate.attorney_notes}"</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            SECTION 0: CALCULATION MODE - THE FORK POINT (TOP OF PAGE)
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
            <h2 className="cs-h2">First, Choose Your Claim Type</h2>
          </div>

          <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <p className="cs-body mb-4">This choice determines your entire workflow and calculation methodology.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Standard BI Mode */}
              <label
                className="relative flex flex-col p-5 rounded-xl cursor-pointer hover:shadow-md transition-all"
                style={{
                  background: 'var(--color-bg-elevated)',
                  border: `1px solid ${formData.calculation_mode === 'standard_bi' ? 'var(--color-brand)' : 'var(--color-border)'}`,
                  boxShadow: formData.calculation_mode === 'standard_bi' ? '0 0 0 1px var(--color-brand)' : undefined,
                }}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="calculation_mode"
                    value="standard_bi"
                    checked={formData.calculation_mode === 'standard_bi'}
                    onChange={(e) => setFormData({ ...formData, calculation_mode: e.target.value })}
                    className="mt-1 w-5 h-5"
                    style={{ accentColor: 'var(--color-brand)' }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>Standard BI Calculator</p>
                    </div>
                    <p className="cs-body">
                      For past interruptions already occurred
                    </p>
                    <div className="mt-3 space-y-1">
                      <p className="cs-meta flex items-center gap-1">
                        <Check className="w-3 h-3" style={{ color: 'var(--color-brand)' }} /> Requires: 4 years financial data
                      </p>
                      <p className="cs-meta flex items-center gap-1">
                        <Check className="w-3 h-3" style={{ color: 'var(--color-brand)' }} /> BI engine with V-Test causation
                      </p>
                      <p className="cs-meta flex items-center gap-1">
                        <Check className="w-3 h-3" style={{ color: 'var(--color-brand)' }} /> RTP recovery adjustment applied
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tooltip button */}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setShowModeInfo(showModeInfo === 'standard' ? null : 'standard'); }}
                  className="absolute top-3 right-3"
                  style={{ color: 'var(--color-text-dim)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-brand)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-dim)'; }}
                >
                  <HelpCircle className="w-4 h-4" />
                </button>

                {formData.calculation_mode === 'standard_bi' && (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
                    <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>✓ Most common for disaster claims</p>
                  </div>
                )}
              </label>

              {/* Future Closure Mode */}
              <label
                className="relative flex flex-col p-5 rounded-xl cursor-pointer hover:shadow-md transition-all"
                style={{
                  background: 'var(--color-bg-elevated)',
                  border: `1px solid ${formData.calculation_mode === 'future_closure' ? 'var(--color-brand)' : 'var(--color-border)'}`,
                  boxShadow: formData.calculation_mode === 'future_closure' ? '0 0 0 1px var(--color-brand)' : undefined,
                }}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="calculation_mode"
                    value="future_closure"
                    checked={formData.calculation_mode === 'future_closure'}
                    onChange={(e) => setFormData({ ...formData, calculation_mode: e.target.value })}
                    className="mt-1 w-5 h-5"
                    style={{ accentColor: 'var(--color-brand)' }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 700 }}>Future Forced Closure</p>
                    </div>
                    <p className="cs-body">
                      For permanent or long-term closures (e.g., plant explosion, train derailment, PFAS)
                    </p>
                    <div className="mt-3 space-y-1">
                      <p className="cs-meta flex items-center gap-1">
                        <Check className="w-3 h-3" style={{ color: 'var(--color-brand)' }} /> Requires: 12+ months historical data
                      </p>
                      <p className="cs-meta flex items-center gap-1">
                        <Check className="w-3 h-3" style={{ color: 'var(--color-brand)' }} /> Monte Carlo probability ranges
                      </p>
                      <p className="cs-meta flex items-center gap-1">
                        <Check className="w-3 h-3" style={{ color: 'var(--color-brand)' }} /> Pro forma {formData.closure_duration_months}-month projection
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tooltip button */}
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setShowModeInfo(showModeInfo === 'future' ? null : 'future'); }}
                  className="absolute top-3 right-3"
                  style={{ color: 'var(--color-text-dim)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-brand)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-dim)'; }}
                >
                  <HelpCircle className="w-4 h-4" />
                </button>

                {formData.calculation_mode === 'future_closure' && (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--color-border-sub)' }}>
                    <p className="cs-meta flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}><AlertCircle className="w-3.5 h-3.5" /> For 2-4 year closure scenarios</p>
                  </div>
                )}
              </label>
            </div>

            {/* Info Tooltips */}
            {showModeInfo === 'standard' && (
              <div className="mt-4 p-4 rounded-xl" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
                  <div className="cs-body">
                    <p className="mb-1" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>Standard BI Calculator Details</p>
                    <ul className="space-y-1 cs-meta">
                      <li>• Uses BI (Historical Period Comparability Check) engine</li>
                      <li>• V-Test causation validates revenue decline matches event period</li>
                      <li>• RTP (Return-to-Pre-event) recovery adjustment projects the recovery ramp</li>
                      <li>• Best for: Hurricanes, floods, temporary shutdowns, supply chain issues</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {showModeInfo === 'future' && (
              <div className="mt-4 p-4 rounded-xl" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
                  <div className="cs-body">
                    <p className="mb-1" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>Future Forced Closure Details</p>
                    <ul className="space-y-1 cs-meta">
                      <li>• Projects lost profits over {formData.closure_duration_months} months with confidence intervals</li>
                      <li>• Monte Carlo simulation provides low/mid/high demand ranges</li>
                      <li>• Includes shutdown costs, restart costs, continuing expenses</li>
                      <li>• Best for: Toxic contamination, plant explosions, permanent closures</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Closure Duration - Only for Future Closure */}
            {isFutureClosureMode && (() => {
              const derivedClosure = deriveClosureFromEventIntel(data);
              return (
              <div className="mt-4 p-4 rounded-xl" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  <div>
                    <p style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>Projected Closure Duration</p>
                    <p className="cs-meta">How long will the business remain closed?</p>
                  </div>
                </div>

                {/* Factual period derived from the claim's confirmed loss + reopening dates.
                    When present, the damages engine uses THIS (not the manual value below). */}
                {derivedClosure && (
                  <div className="mb-3 p-3 rounded-lg" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-brand)' }}>
                    <p style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
                      Closure period from claim data: {formatMonth(derivedClosure.startMonth)} – {formatMonth(derivedClosure.reopenMonth)}
                      {' '}· {derivedClosure.months} month{derivedClosure.months === 1 ? '' : 's'}
                    </p>
                    <p className="cs-meta">
                      Derived from the confirmed loss and reopening dates — the damages engine uses this
                      factual period. The manual value below is a fallback used only if claim dates are missing.
                    </p>
                  </div>
                )}

                <label className="cs-meta" style={{ display: 'block', marginBottom: 4 }}>
                  {derivedClosure ? 'Manual override (fallback)' : 'How long will the business remain closed?'}
                </label>
                <select
                  value={formData.closure_duration_months}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    setFormData({ ...formData, closure_duration_months: newValue });
                    // Save immediately to persist the change
                    if (onDataUpdate) {
                      onDataUpdate({ closure_duration_months: newValue });
                    }
                  }}
                  className="w-full md:w-64 px-4 py-2.5 rounded-xl focus:outline-none"
                  style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text-primary)', fontWeight: 500 }}
                >
                  <option value={1}>1 month</option>
                  <option value={2}>2 months</option>
                  <option value={3}>3 months</option>
                  <option value={4}>4 months</option>
                  <option value={5}>5 months</option>
                  <option value={6}>6 months</option>
                  <option value={9}>9 months</option>
                  <option value={12}>12 months (1 year)</option>
                  <option value={18}>18 months</option>
                  <option value={24}>24 months (2 years)</option>
                  <option value={36}>36 months (3 years)</option>
                  <option value={48}>48 months (4 years)</option>
                  <option value={60}>60 months (5 years)</option>
                </select>
              </div>
              );
            })()}
          </div>
        </div>
        
        {/* Two-column layout for remaining fields */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 1: Event Type
            ═══════════════════════════════════════════════════════════════════ */}
            <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                    <Zap className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  </div>
                  <div>
                    <h3 className="cs-h3">Disruption Event Type <span style={{ color: 'var(--color-needs-human-text)' }}>*</span></h3>
                    <p className="cs-meta">What caused the business interruption?</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEventInfo(!showEventInfo)}
                  className="cs-meta flex items-center gap-1"
                  style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}
                >
                  <Info className="w-4 h-4" />
                  {showEventInfo ? 'Hide' : 'Info'}
                </button>
              </div>

              <select
                value={formData.event_type}
                onChange={(e) => handleEventTypeChange(e.target.value)}
                className="w-full px-4 py-3 rounded-xl focus:outline-none transition-all text-sm"
                style={{ border: `1px solid ${formData.event_type ? 'var(--color-verified)' : 'var(--color-border)'}`, background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)', fontWeight: 500 }}
              >
                <option value="">-- Select Disruption Event --</option>
                {eventTypes.map((event) => (
                  <option key={event.value} value={event.value}>
                    {event.label} {event.threshold !== '0%' ? `(Threshold: ${event.threshold})` : ''}
                  </option>
                ))}
              </select>
              
              {/* Event Description */}
              {selectedEvent && (
                <div className="mt-4 p-4 rounded-xl" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
                  <p className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{selectedEvent.label}</p>
                  <p className="cs-meta mt-1">{selectedEvent.description}</p>
                  {selectedEvent.threshold !== '0%' && (
                    <p className="cs-meta mt-2">
                      <strong>Threshold:</strong> {selectedEvent.threshold} revenue decline required
                    </p>
                  )}
                </div>
              )}

              {/* Auto-suggest for events that need Future Closure */}
              {selectedEvent?.suggestFutureClosure && formData.calculation_mode === 'standard_bi' && (
                <div className="mt-4 p-3 rounded-xl" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
                    <div className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
                      <p style={{ fontWeight: 600 }}>Suggested: Future Closure Mode</p>
                      <p className="mt-0.5">This event type typically requires long-term closure projections.</p>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, calculation_mode: 'future_closure' })}
                        className="mt-2 underline"
                        style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}
                      >
                        Switch to Future Closure →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Info Panel */}
              {showEventInfo && (
                <div className="mt-4 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)', borderLeft: '4px solid var(--color-brand)' }}>
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                    <div className="cs-meta">
                      <p className="mb-1" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>Event-Specific Causation Tests:</p>
                      <ul className="space-y-0.5 ml-2">
                        <li>• Industry-standard thresholds (NOAA, USGS, FEMA)</li>
                        <li>• Higher thresholds for acute events</li>
                        <li>• Recovery lag multipliers applied</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 2: Business Type
            ═══════════════════════════════════════════════════════════════════ */}
            <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                  <Building className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                </div>
                <div>
                  <h3 className="cs-h3">
                    Business Type <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
                  </h3>
                  <p className="cs-meta">Select category</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {businessTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => { setFormData({ ...formData, business_type: type }); setValidationError(null); }}
                    className="py-2.5 px-3 rounded-lg transition-all text-left text-sm"
                    style={{
                      border: `1px solid ${formData.business_type === type ? 'var(--color-brand)' : 'var(--color-border)'}`,
                      background: formData.business_type === type ? 'var(--color-needs-human-tint)' : 'var(--color-bg-elevated)',
                      color: formData.business_type === type ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)',
                      fontWeight: 500,
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{type}</span>
                      {formData.business_type === type && (
                        <Check className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6">
            {/* ═══════════════════════════════════════════════════════════════════
                SECTION 3: Event Coverage Period (Optional)
            ═══════════════════════════════════════════════════════════════════ */}
            <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                  <Calendar className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                </div>
                <div>
                  <h3 className="cs-h3">Event Coverage Period</h3>
                  <p className="cs-meta">Optional - dates of the disruption</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block cs-label mb-2">Start Date</label>
                  <input
                    type="date"
                    value={formData.event_start_date}
                    onChange={(e) => setFormData({ ...formData, event_start_date: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl focus:outline-none text-sm"
                    style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                  />
                </div>
                <div>
                  <label className="block cs-label mb-2">End Date</label>
                  <input
                    type="date"
                    value={formData.event_end_date}
                    onChange={(e) => setFormData({ ...formData, event_end_date: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl focus:outline-none text-sm"
                    style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                  />
                </div>
              </div>

              {formData.event_start_date && formData.event_end_date && (
                <p className="mt-3 cs-meta flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
                  <Check className="w-4 h-4" />
                  {formData.event_start_date} to {formData.event_end_date}
                </p>
              )}
            </div>

            {/* Mode-specific preview */}
            <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)' }}>
                  <span className="text-lg">{isFutureClosureMode ? '☢️' : '📊'}</span>
                </div>
                <div>
                  <h3 className="cs-h3">
                    {isFutureClosureMode ? 'Future Closure Path' : 'Standard BI Path'}
                  </h3>
                  <p className="cs-meta">
                    What comes next
                  </p>
                </div>
              </div>

              <div className="space-y-2 cs-body">
                {isFutureClosureMode ? (
                  <>
                    <p>→ Step 3: Upload Financial Data</p>
                    <p>→ Step 4: Aggression, Base Period, Duration</p>
                    <p>→ Step 5: Expense Continuation & One-Time Costs</p>
                    <p>→ Step 6: Pro Forma Review + Monte Carlo</p>
                    <p>→ Step 7: Future Closure Report</p>
                  </>
                ) : (
                  <>
                    <p>→ Step 3: Upload Financial Data</p>
                    <p>→ Step 4: Payroll & Additional Inputs</p>
                    <p>→ Step 5: BI Review + Causation Test</p>
                    <p>→ Step 6: Final BI Calculation</p>
                    <p>→ Step 7: Standard BI Report</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </WizardV2Layout>
  );
}
